/**
 * Utilitário para injeção dinâmica de scripts de rastreamento
 * Injeta GTM, Meta Pixel, GA4 e código customizado no <head>
 * 
 * @release v1.0.0 - 2026-03-08
 * @description Responsável por injetar scripts de rastreamento na página pública
 *              do chat. Cada método é idempotente — não injeta duplicatas.
 */

import { logger } from '@/lib/logger';

// Referência global para evitar injeção duplicada
declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>;
    fbq: (...args: unknown[]) => void;
    gtag: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}

/**
 * Injeta o script do Google Tag Manager no <head>
 * Também adiciona o noscript iframe no <body>
 * 
 * @param gtmId - ID do container GTM (ex: GTM-XXXXXX)
 * @param initialData - Dados iniciais para o dataLayer (flow_id, visitor_id, etc.)
 */
export function injectGTM(gtmId: string, initialData?: Record<string, unknown>): void {
  if (!gtmId || document.querySelector(`script[data-gtm-id="${gtmId}"]`)) {
    return; // Já injetado ou ID vazio
  }

  logger.info('trackingInjector', `Injetando GTM: ${gtmId}`);

  // Inicializa dataLayer com dados do fluxo
  window.dataLayer = window.dataLayer || [];
  if (initialData) {
    window.dataLayer.push(initialData);
  }

  // Script principal do GTM
  const script = document.createElement('script');
  script.setAttribute('data-gtm-id', gtmId);
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${gtmId}');
  `;
  document.head.appendChild(script);

  // Noscript fallback no body
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
  document.body.appendChild(noscript);
}

/**
 * Injeta o script do Meta Pixel (Facebook) no <head>
 * Dispara PageView automaticamente
 * 
 * @param pixelId - ID do Meta Pixel (ex: 123456789012345)
 */
export function injectMetaPixel(pixelId: string): void {
  if (!pixelId || document.querySelector(`script[data-pixel-id="${pixelId}"]`)) {
    return;
  }

  logger.info('trackingInjector', `Injetando Meta Pixel: ${pixelId}`);

  const script = document.createElement('script');
  script.setAttribute('data-pixel-id', pixelId);
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  // Noscript fallback
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>`;
  document.body.appendChild(noscript);
}

/**
 * Injeta o script do Google Analytics 4 no <head>
 * Configura automaticamente com o Measurement ID
 * 
 * @param measurementId - Measurement ID do GA4 (ex: G-XXXXXXXXXX)
 */
export function injectGA4(measurementId: string): void {
  if (!measurementId || document.querySelector(`script[data-ga4-id="${measurementId}"]`)) {
    return;
  }

  logger.info('trackingInjector', `Injetando GA4: ${measurementId}`);

  // Script gtag.js
  const gtagScript = document.createElement('script');
  gtagScript.async = true;
  gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  gtagScript.setAttribute('data-ga4-id', measurementId);
  document.head.appendChild(gtagScript);

  // Configuração do gtag
  const configScript = document.createElement('script');
  configScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `;
  document.head.appendChild(configScript);
}

/**
 * Injeta código HTML/JS customizado no <head>
 * Útil para scripts de rastreamento adicionais
 * 
 * @param code - Código HTML/JS para injetar
 */
export function injectCustomHeadCode(code: string): void {
  if (!code || document.querySelector('script[data-custom-head]')) {
    return;
  }

  logger.info('trackingInjector', 'Injetando código customizado no head');

  const container = document.createElement('div');
  container.setAttribute('data-custom-head', 'true');
  container.innerHTML = code;

  // Move todos os filhos para o head
  while (container.firstChild) {
    document.head.appendChild(container.firstChild);
  }
}

/**
 * Injeta todos os scripts de rastreamento configurados
 * Método principal chamado pela página pública do chat
 * 
 * @param tracking - Objeto com IDs de rastreamento
 * @param initialData - Dados iniciais para dataLayer (flow_id, visitor_id, etc.)
 */
export function injectAllTracking(
  tracking: {
    gtm_id?: string | null;
    meta_pixel_id?: string | null;
    ga4_measurement_id?: string | null;
    custom_head_code?: string | null;
  } | null | undefined,
  initialData?: Record<string, unknown>
): void {
  if (!tracking) return;

  // Injeta cada script se configurado
  if (tracking.gtm_id) {
    injectGTM(tracking.gtm_id, initialData);
  }

  if (tracking.meta_pixel_id) {
    injectMetaPixel(tracking.meta_pixel_id);
  }

  if (tracking.ga4_measurement_id) {
    injectGA4(tracking.ga4_measurement_id);
  }

  if (tracking.custom_head_code) {
    injectCustomHeadCode(tracking.custom_head_code);
  }
}
